import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Navigation } from "@/components/navigation";
import { Footer } from "@/components/footer";
import { PageBackground } from "@/components/ui/page-background";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { ArrowRight, Home, Briefcase, FolderOpen, Mail } from "lucide-react";

const NotFound = () => {
  useEffect(() => {
    console.error("404 Error: Page not found:", window.location.pathname);
  }, []);

  const quickLinks = [
    { icon: Briefcase, label: "Services", href: "/services", description: "What we build" },
    { icon: FolderOpen, label: "Portfolio", href: "/portfolio", description: "Our work" },
    { icon: Mail, label: "Contact", href: "/contact", description: "Get in touch" },
  ];

  return (
    <PageBackground variant="aurora">
      <SEO
        title="Page Not Found | AffinityAim"
        description="The page you're looking for doesn't exist. Let's help you find what you need."
        canonical="https://www.affinityaim.com/404"
        noindex={true}
      />
      <Navigation />
      
      <main className="min-h-[80vh] flex items-center justify-center pt-24 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            
            {/* 404 number with gradient */}
            <div className="mb-8 animate-fade-in">
              <span 
                className="font-display font-bold bg-gradient-to-b from-foreground via-foreground/80 to-foreground/20 bg-clip-text text-transparent"
                style={{ 
                  fontSize: 'clamp(120px, 25vw, 200px)', 
                  lineHeight: '1',
                  letterSpacing: '-0.05em'
                }}
              >
                404
              </span>
            </div>

            {/* Message */}
            <div className="mb-12 animate-fade-in" style={{ animationDelay: '100ms' }}>
              <h1 
                className="font-display font-semibold text-foreground mb-4"
                style={{ fontSize: 'clamp(24px, 4vw, 32px)', lineHeight: '1.2' }}
              >
                This page doesn't exist
              </h1>
              <p className="text-muted-foreground max-w-md mx-auto">
                The page you're looking for may have been moved or deleted. Let's get you back on track.
              </p>
            </div>

            {/* Primary CTAs */}
            <div 
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fade-in"
              style={{ animationDelay: '200ms' }}
            >
              <Link to="/">
                <Button variant="default" size="lg" className="group w-full sm:w-auto">
                  <Home className="w-4 h-4 mr-2" />
                  Go to homepage
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" size="lg" className="w-full sm:w-auto group">
                  View our services
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>

            {/* Quick links */}
            <div 
              className="animate-fade-in"
              style={{ animationDelay: '300ms' }}
            >
              <p className="text-sm text-muted-foreground mb-6">Or jump to:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {quickLinks.map((link) => (
                  <Link 
                    key={link.href} 
                    to={link.href}
                    className="group bg-surface/50 border border-border rounded-xl p-5 hover:border-primary/30 hover:bg-surface/80 transition-all duration-300"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                      <link.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="font-medium text-foreground mb-1">{link.label}</div>
                    <div className="text-xs text-muted-foreground">{link.description}</div>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </main>

      <Footer />
    </PageBackground>
  );
};

export default NotFound;